/**
 * Client-side image compression for photo uploads.
 * Targets max 1600px on long edge, 80% quality, max 1MB.
 * (Spec calls out 2400px / quality 0.82 / 10MB cap — we use the more
 * aggressive 1600/0.8 default for the SPA's local case and let the spec's
 * 2400 cap be the server-side maximum on PUT.)
 *
 * On compression failure, returns the original file with `fallback: true`
 * so the UI can show the one-time inline notice "Your browser couldn't
 * slim this photo." (UX-AC-053 / AC-073).
 */
import imageCompression from 'browser-image-compression';

export interface CompressResult {
  /** The blob to upload. May be the original on `fallback: true`. */
  file: File;
  fallback: boolean;
  /** Original byte count. */
  originalBytes: number;
  /** Final byte count. */
  finalBytes: number;
}

const DEFAULT_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1600,
  initialQuality: 0.8,
  useWebWorker: true,
  alwaysKeepResolution: false,
};

export async function compressImage(
  file: File,
  options?: Partial<typeof DEFAULT_OPTIONS>,
): Promise<CompressResult> {
  const merged = { ...DEFAULT_OPTIONS, ...(options ?? {}) };
  const originalBytes = file.size;
  try {
    const compressed: File | Blob = await imageCompression(file, merged);
    // imageCompression returns a Blob in some versions; wrap as File for upload.
    const out: File =
      compressed instanceof File
        ? compressed
        : new File([compressed], file.name, {
            type: (compressed as Blob).type || file.type,
          });
    return {
      file: out,
      fallback: false,
      originalBytes,
      finalBytes: out.size,
    };
  } catch {
    return {
      file,
      fallback: true,
      originalBytes,
      finalBytes: file.size,
    };
  }
}
